import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { assessmentApi } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScoreCircle, ScoreCategory, SectionBar } from '../components/ScoreDisplay';
import { generatePDF } from '../utils/pdfGenerator';
import {
  ArrowRight,
  Download,
  Mail,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  FileText,
  Loader2,
  ChevronRight
} from 'lucide-react';

const SECTION_MAX_SCORES = {
  A: { founder: 30, viability: 20, validation: 20, projections: 15, security: 15 },
  B: { serviceability: 35, security: 20, trading: 20, management: 15, purpose: 10 },
  C: { financial: 30, forecasting: 30, governance: 20, strategic: 20 }
};

const SECTION_LABELS = {
  A: { founder: 'Founder Capability', viability: 'Business Viability', validation: 'Market Validation', projections: 'Financial Strategy', security: 'Security' },
  B: { serviceability: 'Serviceability', security: 'Security', trading: 'Trading History', management: 'Management', purpose: 'Funding Purpose' },
  C: { financial: 'Financial Strength', forecasting: 'Forecasting', governance: 'Governance', strategic: 'Strategic Risk' }
};

export default function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checklistProgress, setChecklistProgress] = useState({});
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAssessment();
    }
  }, [id]);

  const fetchAssessment = async () => {
    try {
      const response = await assessmentApi.getAssessment(id);
      setAssessment(response.data);
      setChecklistProgress(response.data.checklist_progress || {});
    } catch (error) {
      console.error('Failed to fetch assessment:', error);
      toast.error('Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistChange = async (itemId, checked) => {
    const newProgress = { ...checklistProgress, [itemId]: checked };
    setChecklistProgress(newProgress);
    
    try {
      await assessmentApi.updateAssessment(id, { checklist_progress: newProgress });
    } catch (error) {
      console.error('Failed to update checklist:', error);
    }
  };

  const handleDownloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      await generatePDF(assessment, checklistProgress);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleEmailReport = () => {
    toast.info('Email functionality coming soon!');
  };

  const getChecklistCompletion = () => {
    if (!assessment?.checklist) return 0;
    const completed = assessment.checklist.filter(item => checklistProgress[item.id]).length;
    return Math.round((completed / assessment.checklist.length) * 100);
  };

  const getScoreInterpretation = (score) => {
    if (score >= 85) return "Excellent position for funding. You're likely to have strong negotiating power with lenders.";
    if (score >= 70) return "Good funding position. You should be able to access a range of lending options.";
    if (score >= 50) return "Some preparation needed. Address the gaps identified to strengthen your application.";
    return "Significant preparation required before approaching lenders. Focus on the improvement areas below.";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Assessment Not Found</h2>
            <p className="text-slate-600 mb-6">The assessment you're looking for doesn't exist.</p>
            <Link to="/assessment">
              <Button className="btn-primary">Start New Assessment</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const path = assessment.path;
  const sectionScores = assessment.section_scores;
  const maxScores = SECTION_MAX_SCORES[path];
  const sectionLabels = SECTION_LABELS[path];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-8 md:py-12" data-testid="results-page">
        <div className="container-main">
          {/* Score Hero Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12 mb-8"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-center md:text-left">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading mb-2">
                  {assessment.business_name}
                </h1>
                <p className="text-slate-600 mb-6">Funding Readiness Assessment</p>
                
                <ScoreCategory category={assessment.score_category} />
                
                <p className="mt-6 text-slate-600 leading-relaxed">
                  {getScoreInterpretation(assessment.total_score)}
                </p>
              </div>
              
              <div className="flex justify-center">
                <ScoreCircle score={assessment.total_score} size={220} />
              </div>
            </div>
          </motion.section>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Section Breakdown */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="result-card"
              >
                <h2 className="text-xl font-semibold text-slate-900 font-heading mb-6">
                  Score Breakdown
                </h2>
                <div className="space-y-5">
                  {Object.entries(sectionScores).map(([key, score], index) => (
                    <SectionBar
                      key={key}
                      name={sectionLabels[key]}
                      score={score}
                      maxScore={maxScores[key]}
                      delay={index * 0.1}
                    />
                  ))}
                </div>
              </motion.section>

              {/* Strengths */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="result-card"
              >
                <div className="result-card-header">
                  <div className="result-card-icon bg-emerald-100">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-heading">
                    Strengths
                  </h2>
                </div>
                <div className="space-y-4">
                  {assessment.strengths.map((strength, index) => (
                    <div key={index} className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold text-emerald-900 mb-1">{strength.section}</div>
                          <div className="text-sm text-emerald-800 leading-relaxed">{strength.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* Gaps & Recommendations */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="result-card"
              >
                <div className="result-card-header">
                  <div className="result-card-icon bg-amber-100">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-heading">
                    Areas for Improvement
                  </h2>
                </div>
                <div className="space-y-5">
                  {assessment.gaps.map((gap, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                      {/* Header */}
                      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                          </div>
                          <h3 className="font-semibold text-slate-900">{gap.section}</h3>
                          {gap.score_percentage !== undefined && (
                            <span className="ml-auto text-sm font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {gap.score_percentage.toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-5 space-y-4">
                        {gap.issue && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Issue</div>
                            <p className="text-slate-700 leading-relaxed">{gap.issue}</p>
                          </div>
                        )}
                        {gap.action && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Recommended Action</div>
                            <p className="text-slate-700 leading-relaxed">{gap.action}</p>
                          </div>
                        )}
                        {gap.eyb_help && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <div className="flex items-start gap-3 bg-sky-50 rounded-lg p-4 border border-sky-100">
                              <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <TrendingUp className="w-3.5 h-3.5 text-sky-600" />
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-sky-700 mb-1">How EYB Can Help</div>
                                <p className="text-sm text-sky-800 leading-relaxed">{gap.eyb_help}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Fallback for old format */}
                        {!gap.issue && gap.description && (
                          <p className="text-slate-700">{gap.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.section>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Actions */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="result-card"
              >
                <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">
                  Actions
                </h2>
                <div className="space-y-3">
                  <Button 
                    className="w-full btn-primary justify-between"
                    onClick={handleDownloadPDF}
                    disabled={generatingPdf}
                    data-testid="download-pdf-btn"
                  >
                    {generatingPdf ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="flex items-center gap-2">
                          <Download className="w-4 h-4" />
                          Export PDF Report
                        </span>
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    onClick={handleEmailReport}
                    data-testid="email-report-btn"
                  >
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Report
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Link to="/assessment" className="block">
                    <Button variant="outline" className="w-full justify-between" data-testid="new-assessment-btn">
                      <span className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        New Assessment
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </motion.section>

              {/* Document Checklist */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="result-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900 font-heading">
                    Document Checklist
                  </h2>
                  <span className="text-sm font-medium text-sky-600">
                    {getChecklistCompletion()}% Complete
                  </span>
                </div>
                
                <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
                  <div 
                    className="bg-sky-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${getChecklistCompletion()}%` }}
                  />
                </div>

                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {assessment.checklist.map((item) => (
                    <div
                      key={item.id}
                      className={`checklist-item ${checklistProgress[item.id] ? 'completed' : ''}`}
                    >
                      <Checkbox
                        id={item.id}
                        checked={checklistProgress[item.id] || false}
                        onCheckedChange={(checked) => handleChecklistChange(item.id, checked)}
                        data-testid={`checklist-${item.id}`}
                      />
                      <label
                        htmlFor={item.id}
                        className={`text-sm cursor-pointer ${
                          checklistProgress[item.id] ? 'text-slate-500 line-through' : 'text-slate-700'
                        }`}
                      >
                        {item.item}
                        {item.required && !checklistProgress[item.id] && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* Assessment Info */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="result-card bg-slate-50"
              >
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Assessment Details
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Business Type</span>
                    <span className="font-medium text-slate-900 capitalize">
                      {assessment.business_status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Funding Amount</span>
                    <span className="font-medium text-slate-900">
                      {assessment.loan_amount_band.replace(/_/g, ' ').replace('under', 'Under ').replace('above', 'Above ').replace('250k to 1m', '$250k - $1M')}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-slate-500">Purpose</span>
                    <div className="flex flex-wrap gap-1">
                      {(assessment.funding_purposes || [assessment.funding_purpose]).map((purpose, idx) => (
                        <span key={idx} className="badge info capitalize">
                          {(purpose || '').replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Date</span>
                    <span className="font-medium text-slate-900">
                      {new Date(assessment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 text-center">
            <p className="text-xs text-slate-400 max-w-2xl mx-auto">
              EYB Consulting provides business advisory services only and does not provide credit advice. 
              Lending decisions are made solely by the lender. This assessment is for informational 
              purposes only and does not guarantee funding approval.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
